import { useConsentStatusQuery } from "../../generated.hooks";
import ReConsentDialog from "../legal/ReConsentDialog";
import React from "react";

/**
 * Holds authenticated users at a re-consent prompt when a published policy has
 * been superseded since they last accepted.
 *
 * Renders children on failure rather than blocking. `requiresReconsent` is
 * false while nothing is published, so the common case is a no-op, and a
 * transient query error should not lock people out of the product - the
 * server enforces the same rule on the mutations that matter, so the UI does
 * not have to fail closed to be correct.
 *
 * The children render underneath the prompt rather than being swapped out for
 * it. Replacing them also removed the layout that paints the themed page
 * background, so the gate appeared as a dark card on an unthemed body; a modal
 * over the dimmed app inherits the theme and keeps the user oriented. The
 * dialog is not dismissible, so the app behind it stays inert.
 *
 * This owns the consent query and hands the result down, rather than letting
 * the prompt query independently. The generated hooks default to
 * `fetchPolicy: "no-cache"`, so two components asking the same question get
 * two unrelated results: the prompt could refetch itself clear after accepting
 * while this guard still held the stale answer, leaving the user stuck behind
 * a modal with nothing left to accept and a disabled button.
 */
function ConsentGuard({ children }) {
  const { data, loading, error, refetch } = useConsentStatusQuery();

  if (loading || error) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  return (
    <React.Fragment>
      {children}
      {data?.consentStatus?.requiresReconsent && (
        <ReConsentDialog
          pendingPolicies={data.consentStatus.pendingPolicies ?? []}
          onAccepted={refetch}
        />
      )}
    </React.Fragment>
  );
}

export default ConsentGuard;
